import React, { useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { FaBook, FaIdCard, FaGraduationCap } from "react-icons/fa";
import GenericCrudPage from '../components/GenericCrudPage';
import { usePrograms } from "../hooks/useDropdownData";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const DisciplinePage = () => {
  // Fix: Use the correct hook name that returns 'programmes'
  const { programmes: programs, loading: programsLoading } = usePrograms();

  // Configuration for the generic CRUD operations
  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/master/getDiscipline',
      save: '/master/saveDiscipline',
      update: '/master/updateDiscipline',
      delete: '/master/deleteDiscipline'
    },
    validateForm: (form) => {
      const errors = {};
      if (!form.exName?.trim()) errors.exName = "Discipline Name is required";
      if (form.exName && form.exName.length > 100) errors.exName = "Discipline Name must be less than 100 characters";
      if (!form.exIdentifier?.trim()) errors.exIdentifier = "Identifier is required";
      if (!form.programmes) errors.programmes = "Program is required";
      return errors;
    },
    initialFormState: {
      exName: "", 
      exIdentifier: "", 
      programmes: "", 
      exIsActive: true, 
      user: ""
    },
    entityName: 'Discipline'
  };

  // Form fields configuration using useMemo to optimize
  const formFields = useMemo(() => {
    console.log("Programs data:", programs); // Debug log
    
    // Create options array with better error handling
    const getSelectOptions = () => {
      if (programsLoading) {
        return [{ value: '', label: '-- Loading Programs --' }];
      }
      
      if (!programs || programs.length === 0) {
        return [{ value: '', label: '-- No Programs Found --' }];
      }
      
      const options = [{ value: '', label: '-- Select Program --' }];
      
      // Handle different data structures
      programs.forEach(program => {
        if (program && (program.id || program.value)) {
          options.push({
            value: program.id || program.value,
            label: program.exName || program.name || program.label || `Program ${program.id}`
          });
        }
      });
      
      return options;
    };

    return [
      { 
        name: "exName", 
        label: "Discipline Name", 
        icon: FaBook, 
        required: true, 
        type: "text",
        placeholder: "Enter discipline name"
      },
      { 
        name: "exIdentifier", 
        label: "Identifier", 
        icon: FaIdCard, 
        required: true, 
        type: "text",
        placeholder: "Enter unique identifier"
      },
      {
        name: "programmes",
        label: "Program",
        icon: FaGraduationCap,
        required: true,
        as: "select",
        options: getSelectOptions(),
        disabled: programsLoading
      },
      { 
        name: 'exIsActive', 
        label: 'Active Status', 
        icon: FaIdCard, 
        as: 'switch' 
      },
    ];
  }, [programs, programsLoading]);

  // Transform form data to API payload
  const transformPayload = (form, editId, userId) => {
    const payload = {
      exName: form.exName?.trim() || "",
      exIdentifier: form.exIdentifier?.trim() || "",
      programmes: form.programmes ? parseInt(form.programmes, 10) : null,
      exIsActive: form.exIsActive ? 1 : 0,
      user: userId || "",
    };
    
    if (editId) {
      payload.id = editId;
    }
    
    return payload;
  };

  // Transform API data to form for editing
  const transformToForm = (discipline) => ({
    exName: discipline?.exName || "",
    exIdentifier: discipline?.exIdentifier || "",
    programmes: discipline?.programmesId?.toString() || discipline?.programmes?.toString() || "",
    exIsActive: discipline?.exIsActive === 1 || discipline?.exIsActive === true,
    user: discipline?.user || "",
  });

  // Get view modal fields with better program name resolution
  const getViewFields = (discipline) => {
    if (!discipline) return [];
    
    const findProgramName = (id) => {
      if (!id || !programs) return '-';
      
      const program = programs.find(item => 
        String(item.id) === String(id) || 
        String(item.value) === String(id)
      );
      
      return program?.exName || program?.name || program?.label || '-';
    };

    return [
      { 
        label: "Discipline Name", 
        value: discipline.exName || '-', 
        icon: FaBook 
      },
      { 
        label: "Identifier", 
        value: discipline.exIdentifier || '-', 
        icon: FaIdCard 
      },
      {
        label: "Program",
        value: discipline.programmesName || findProgramName(discipline.programmesId),
        icon: FaGraduationCap
      },
      { 
        label: 'Status', 
        value: discipline.exIsActive ? 'Active' : 'Inactive', 
        icon: FaIdCard 
      },
    ];
  };

  const tableColumns = [
    { 
      key: 'exName', 
      label: 'Discipline Name', 
      sortable: true 
    },
    { 
      key: 'exIdentifier', 
      label: 'Identifier', 
      sortable: true 
    },
    {
      key: 'programmesName',
      label: 'Program',
      sortable: false,
      render: (item) => {
        // First try to get the program name directly from the item
        if (item.programmesName) return item.programmesName;
        if (item.programName) return item.programName;
        if (item.programsName) return item.programsName;
        
        // If not available, find by ID in programs array
        if (programs && (item.programmesId || item.programId)) {
          const program = programs.find(p => 
            p.id === item.programmesId || 
            p.id === item.programId ||
            p.value === item.programmesId ||
            p.value === item.programId
          );
          
          if (program) {
            return program.exName || program.name || program.label;
          }
        }
        
        return "-";
      }
    },
    {
      key: 'exIsActive',
      label: 'Status',
      sortable: true,
      render: (item) => (
        <span className={`badge ${item.exIsActive ? 'bg-success' : 'bg-secondary'}`}>
          {item.exIsActive ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  // Show a loading spinner while dropdown data is being fetched
  if (programsLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading programs...</p>
      </div>
    );
  }

  // Debug: Show programs data if available
  if (programs && programs.length > 0) {
    console.log("Available programs:", programs);
  } else if (!programsLoading) {
    console.warn("No programs found or programs array is empty");
  }

  return (
    <GenericCrudPage
      title="Discipline"
      config={config}
      formFields={formFields}
      tableColumns={tableColumns}
      getViewFields={getViewFields}
      transformPayload={transformPayload}
      transformToForm={transformToForm}
      emptyIcon={FaBook}
      searchPlaceholder="Search by name, identifier, program..."
      csvHeaders={[
        { key: 'exName', label: 'Discipline Name' },
        { key: 'exIdentifier', label: 'Identifier' },
        { 
          key: 'programmesName', 
          label: 'Program', 
          formatter: (value, row) => {
            // Try different possible field names
            if (row.programmesName) return row.programmesName;
            if (row.programName) return row.programName;
            if (row.programsName) return row.programsName;
            
            // Find by ID if name not available
            if (programs && (row.programmesId || row.programId)) {
              const program = programs.find(p => 
                p.id === row.programmesId || 
                p.id === row.programId ||
                p.value === row.programmesId ||
                p.value === row.programId
              );
              return program?.exName || program?.name || program?.label || '-';
            }
            
            return '-';
          }
        },
        { 
          key: 'exIsActive', 
          label: 'Status', 
          formatter: (value) => (value ? "Active" : "Inactive") 
        }
      ]}
      csvFilename="disciplines.csv"
    />
  );
};

export default DisciplinePage;